import { memo, useState } from "react";
import { useDispatch } from "react-redux";

import { Popover } from "antd";
import { EvaChart, RecoveryPVIRadius, RecoveryPVISource } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { updateChartByPath } from "store/features";
import styled from "styled-components";

import { useChartEntities } from "hooks/charts";

import { Radius_Icon } from "components/icons";

import ToolbarButton from "../../../base/ToolbarButton";

const RADIUS_KEY_PREFIX = "radius:";
const SOURCE_KEY_PREFIX = "source:";

interface MenuOption {
  key: string;
  label: string;
}

const RADIUS_OPTIONS: MenuOption[] = [
  { key: `${RADIUS_KEY_PREFIX}${RecoveryPVIRadius.Gross}`, label: "Gross" },
  { key: `${RADIUS_KEY_PREFIX}${RecoveryPVIRadius.SweetSpot}`, label: "Sweet Spot" },
  { key: `${RADIUS_KEY_PREFIX}${RecoveryPVIRadius.LandingZone}`, label: "Landing Zone" }
];

const SOURCE_OPTIONS: MenuOption[] = [
  { key: `${SOURCE_KEY_PREFIX}${RecoveryPVISource.AllSources}`, label: "All Sources" },
  { key: `${SOURCE_KEY_PREFIX}${RecoveryPVISource.Private}`, label: "Private" },
  { key: `${SOURCE_KEY_PREFIX}${RecoveryPVISource.EvaResearch}`, label: "EVA Research" }
];

const RecoveryPVIRadiusToggle = () => {
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { chartType, recoveryPVIRadius } = useChartEntities(id);

  if (chartType !== EvaChart.RecoveryPVI && chartType !== EvaChart.RecoveryTime)
    return null;

  const radiusValue =
    (recoveryPVIRadius?.properties?.value as RecoveryPVIRadius) ??
    RecoveryPVIRadius.Gross;
  const sourceValue =
    (recoveryPVIRadius?.properties?.source as RecoveryPVISource) ??
    RecoveryPVISource.AllSources;

  function handleMenuClick(key: string) {
    if (key.startsWith(RADIUS_KEY_PREFIX)) {
      dispatch(
        updateChartByPath({
          id,
          path: "features.recoveryPVIRadius.properties.value",
          value: key.slice(RADIUS_KEY_PREFIX.length) as RecoveryPVIRadius
        })
      );
    } else if (key.startsWith(SOURCE_KEY_PREFIX)) {
      dispatch(
        updateChartByPath({
          id,
          path: "features.recoveryPVIRadius.properties.source",
          value: key.slice(SOURCE_KEY_PREFIX.length) as RecoveryPVISource
        })
      );
    }
  }

  const radiusKey = `${RADIUS_KEY_PREFIX}${radiusValue}`;
  const sourceKey = `${SOURCE_KEY_PREFIX}${sourceValue}`;

  const menuContent = (
    <MenuWrapper>
      <MenuGroupTitle>Radius Type</MenuGroupTitle>
      {RADIUS_OPTIONS.map((opt) => (
        <MenuItem
          key={opt.key}
          selected={opt.key === radiusKey}
          onClick={() => handleMenuClick(opt.key)}>
          {opt.label}
        </MenuItem>
      ))}
      <MenuDivider />
      <MenuGroupTitle>Source</MenuGroupTitle>
      {SOURCE_OPTIONS.map((opt) => (
        <MenuItem
          key={opt.key}
          selected={opt.key === sourceKey}
          onClick={() => handleMenuClick(opt.key)}>
          {opt.label}
        </MenuItem>
      ))}
    </MenuWrapper>
  );

  return (
    <Popover
      arrowPointAtCenter
      content={menuContent}
      onOpenChange={setIsOpen}
      open={isOpen}
      overlayClassName="radius-popover"
      placement="bottomRight"
      trigger="click">
      <ToolbarButton
        data-testid="recovery-pvi-radius-trigger"
        aria-label={radiusValue}
        active={isOpen}
        icon={<Radius_Icon />}
        isMenuButton={true}
        overflowLabel={radiusValue}
        tooltipText="Select a Geo Radius — OOIP, Porosity, and Volume are derived from the selected radius"
      />
    </Popover>
  );
};

export default memo(RecoveryPVIRadiusToggle);

// ─── Styled components ───────────────────────────────────────────────────────

const MenuWrapper = styled.div`
  background: #fff;
  border-radius: var(--border-radius, 4px);
  width: fit-content;
  padding: 4px 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
`;

const MenuGroupTitle = styled.div`
  padding: 5px 12px 4px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  font-weight: 400;
  cursor: default;
  user-select: none;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 4px 0;
`;

const MenuItem = styled.div<{ selected?: boolean }>`
  padding: 5px 12px;
  cursor: pointer;
  background: ${({ selected }) => (selected ? "var(--color-primary)" : "transparent")};
  color: ${({ selected }) => (selected ? "#fff" : "rgba(0, 0, 0, 0.85)")};
  font-weight: ${({ selected }) => (selected ? 500 : 400)};
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ selected }) =>
      selected ? "var(--color-primary-hover)" : "rgba(0, 0, 0, 0.04)"};
  }
`;
